import React from "react"
import { type ApolloError } from "@apollo/client"
import { isNetworkRequestInFlight, type NetworkStatus } from "@apollo/client/core/networkStatus"
import {
  DirectionFromOrigin,
  type EntityParty,
  ProductArea,
  type TransactionSummary,
  useSummarizeTransactionsByPartyQuery,
} from "@digits-graphql/frontend/graphql-bearer"
import { molecule, use, useMolecule } from "bunshi/react"
import { atom, useSetAtom } from "jotai"
import { LegalEntityIdScope } from "src/frontend/components/LegalEntityIdScope"
import { usePartyRoles } from "src/frontend/components/OS/Applications/VendorDirectory/hooks/usePartyRoles"
import { useVendorCustomerCustomization } from "src/frontend/components/OS/Applications/VendorDirectory/hooks/useVendorCustomerCustomization"
import { VendorDirectoryScope } from "src/frontend/components/OS/Applications/VendorDirectory/VendorScopeProvider"
import { useViewVersion } from "src/frontend/components/Shared/Contexts/useViewVersion"
import useIntervalOrigin from "src/shared/hooks/useIntervalOrigin"

/*
  MOLECULES
*/

// Data to support filter accordion
export const vendorDataMolecule = molecule(() => {
  // Vendor directory scope does not provide a value that changes, so it just specifies the scope boundary for vendor directory data
  use(VendorDirectoryScope)
  use(LegalEntityIdScope)

  const partyDataAtom = atom<PartySummary[] | undefined>(undefined)
  const errorAtom = atom<ApolloError | undefined>(undefined)
  const entityPartiesAtom = atom(
    (get) => get(partyDataAtom)?.map((party) => party.partyObject) ?? []
  )
  const partyIdsAtom = atom((get) => get(entityPartiesAtom).map((party) => party.id) ?? [])
  const totalCountMapAtom = atom((get) => {
    const map = new Map<string, number>()
    const partyData = get(partyDataAtom)
    partyData?.reduce((acc, party) => {
      acc.set(party.partyObject.id, party.summary.total.transactionsCount)
      return acc
    }, map)

    return map
  })

  const totalCountAtom = atom((get) => {
    const partyData = get(partyDataAtom)
    return partyData?.reduce((acc, party) => acc + party.summary.total.transactionsCount, 0) ?? 0
  })
  const loadingAtom = atom((get) => isNetworkRequestInFlight(get(networkStatusAtom)))
  const networkStatusAtom = atom<NetworkStatus>()

  return {
    loadingAtom,
    errorAtom,
    partyDataAtom,
    entityPartiesAtom,
    partyIdsAtom,
    totalCountMapAtom,
    totalCountAtom,
    networkStatusAtom,
  }
})

/*
  INTERFACES
*/

interface PartySummary {
  partyObject: EntityParty
  summary: TransactionSummary
}

/*
  HYDRATORS
*/

export const useHydrateVendorDataMolecule = () => {
  const intervalOrigin = useIntervalOrigin()
  const viewKey = useViewVersion()
  const partyRoles = usePartyRoles()
  const { defaultCategoryType } = useVendorCustomerCustomization()

  const { partyDataAtom, networkStatusAtom, errorAtom } = useMolecule(vendorDataMolecule)
  const setPartyData = useSetAtom(partyDataAtom)
  const setNetworkStatus = useSetAtom(networkStatusAtom)
  const setError = useSetAtom(errorAtom)
  const { data, networkStatus, error } = useSummarizeTransactionsByPartyQuery({
    variables: {
      filter: {
        viewKey,
        productArea: ProductArea.None,
        partyRoles,
      },
      direction: DirectionFromOrigin.Past,
      origin: {
        ...intervalOrigin,
        intervalCount: intervalOrigin?.intervalCount || 1,
      },
      defaultCategoryType,
    },
    notifyOnNetworkStatusChange: true,
  })

  React.useEffect(() => {
    if (!isNetworkRequestInFlight(networkStatus)) {
      setPartyData(data?.dimensionalTransactionSummary?.party)
    }
  }, [data, setPartyData, networkStatus])

  React.useEffect(() => {
    setNetworkStatus(networkStatus)
  }, [networkStatus, setNetworkStatus])

  React.useEffect(() => {
    setError(error)
  }, [error, setError])
}
